import DynamicIcon from "@/components/utils/lucide-icon";
import * as Icons from "lucide-react";
import { LucideIcon } from "lucide-react";
import type { LucideIconName } from "@/convex/lib/modules";

/**
 * Type for icon names from lucide-react
 * This matches the keyof typeof Icons from lucide-react
 */
export type IconName = keyof typeof Icons;

/**
 * Validate that a LucideIconName (string from convex) is a valid IconName
 */
export function isLucideIconName(name: LucideIconName): name is IconName {
  return name in Icons;
}

/**
 * Props for ModuleIcon component
 */
interface ModuleIconProps {
  /** Icon name from lucide-react (e.g., "Settings", "ShoppingCart") */
  iconName: string;
  /** Additional CSS classes */
  className?: string;
  /** Icon size in pixels */
  size?: number;
}

/**
 * Component wrapper for module icons
 * Uses DynamicIcon internally to render lucide-react icons
 *
 * @example
 * <ModuleIcon iconName="Settings" className="h-4 w-4" />
 * <ModuleIcon iconName="ShoppingCart" size={24} />
 */
export function ModuleIcon({ iconName, className, size }: ModuleIconProps) {
  // Validate that the icon exists in lucide-react
  if (!isLucideIconName(iconName)) {
    console.warn(
      `Icon "${iconName}" not found in lucide-react, using fallback`,
    );
    return <DynamicIcon name="HelpCircle" className={className} size={size} />;
  }

  return <DynamicIcon name={iconName} className={className} size={size} />;
}

/**
 * Get icon component from lucide-react by name
 * Returns the actual component class, not an instance
 *
 * @example
 * const SettingsIcon = getIconComponent("Settings");
 * return <SettingsIcon className="h-4 w-4" />;
 */
export function getIconComponent(iconName: string): LucideIcon {
  if (iconName in Icons) {
    return Icons[iconName as IconName] as LucideIcon;
  }

  console.warn(
    `Icon "${iconName}" not found in lucide-react, using HelpCircle`,
  );
  return Icons.HelpCircle;
}

/**
 * Verify if an icon name exists in lucide-react
 * @deprecated Use isLucideIconName instead for better type safety
 */
export function isValidIconName(iconName: string): iconName is IconName {
  return iconName in Icons;
}

/**
 * Get all available icon names from lucide-react
 * Useful for debugging or validation
 */
export function getAllIconNames(): IconName[] {
  return Object.keys(Icons) as IconName[];
}

/**
 * Map module icon string to React component
 * This is the main function to use when rendering navigation items
 *
 * @example
 * const navigation = [
 *   { name: "Settings", icon: getModuleIconComponent("Settings") }
 * ];
 */
export function getModuleIconComponent(
  iconName: string,
): React.ComponentType<{ className?: string }> {
  return getIconComponent(iconName);
}
